import React from 'react';

const PrivacyPolicy = () => {
  return (
    <div className="page-legal">
      <div className="container">
        <div className="legal-content">
          <h1 className="heading-1">Privacy Policy</h1>
          <p className="legal-updated">Last Updated: January 2025</p>
          
          <p className="body-regular legal-intro">
            At Glow Health, we understand that your health information is deeply personal. 
            This Privacy Policy explains how we collect, use, and protect your information 
            when you use our website and services. We are committed to maintaining your trust 
            through transparent, patient-centered privacy practices.
          </p>

          <section className="legal-section">
            <h2 className="heading-3">Information We Collect</h2>
            <p className="body-regular">
              We collect information to provide you with personalized hormone health care. 
              This includes:
            </p>
            <ul className="legal-list">
              <li><strong>Personal Information:</strong> Name, email address, phone number, date of birth, and state of residence</li>
              <li><strong>Health Information:</strong> Symptoms, health history, current medications, supplements, and hormone testing history submitted through our intake forms</li>
              <li><strong>Scheduling Information:</strong> Appointment preferences and scheduling details</li>
              <li><strong>Payment Information:</strong> Billing details processed securely through our payment provider</li>
              <li><strong>Communications:</strong> Messages, questions, and requests submitted through our patient portal</li>
              <li><strong>Website Usage:</strong> How you interact with our website to improve your experience</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2 className="heading-3">How We Use Your Information</h2>
            <p className="body-regular">Your information helps us provide you with quality care:</p>
            <ul className="legal-list">
              <li><strong>Care Coordination:</strong> To prepare for your consultations and develop personalized treatment plans</li>
              <li><strong>Provider Review:</strong> To enable our licensed providers to review your health history and symptoms</li>
              <li><strong>Communication:</strong> To send appointment confirmations, care updates, and respond to your questions</li>
              <li><strong>Operations:</strong> To process payments, manage scheduling, and maintain records</li>
              <li><strong>Improvement:</strong> To enhance our services and your overall experience</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2 className="heading-3">Third-Party Services</h2>
            <p className="body-regular">
              We work with trusted partners to deliver our services securely:
            </p>
            <ul className="legal-list">
              <li><strong>Scheduling:</strong> We use secure scheduling tools to manage appointments</li>
              <li><strong>Payments:</strong> Payment processing is handled by Stripe, a PCI-compliant payment provider</li>
              <li><strong>Data Storage:</strong> Your information is stored using secure, encrypted systems</li>
              <li><strong>Communication Tools:</strong> We use secure platforms for patient messaging and care coordination</li>
            </ul>
            <p className="body-regular">
              We carefully select partners who maintain strong privacy and security standards. 
              We do not sell your personal or health information to third parties.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="heading-3">Cookie & Tracking Practices</h2>
            <p className="body-regular">
              Glow Health LLC uses cookies and similar tracking technologies to improve your 
              experience on our website, analyze site traffic, and support our marketing efforts.
            </p>
            
            <h3 className="heading-4" style={{ marginTop: '20px', marginBottom: '12px', fontSize: '16px', fontWeight: '600' }}>What Are Cookies?</h3>
            <p className="body-regular">
              Cookies are small text files stored on your device when you visit a website. They 
              help us remember your preferences, understand how you use our site, and deliver 
              relevant content and advertisements.
            </p>
            
            <h3 className="heading-4" style={{ marginTop: '20px', marginBottom: '12px', fontSize: '16px', fontWeight: '600' }}>Types of Cookies & Tracking Technologies We Use</h3>
            <ul className="legal-list">
              <li><strong>Essential Cookies:</strong> Required for basic website functionality, such as maintaining your session and remembering your preferences.</li>
              <li><strong>Analytics Cookies:</strong> Help us understand how visitors interact with our website, which pages are most popular, and how users navigate through the site. We use Google Analytics for this purpose.</li>
              <li><strong>Advertising & Tracking Pixels:</strong> Allow us to measure the effectiveness of our advertising campaigns and deliver personalized ads. We use Meta Pixel (Facebook/Instagram) to track conversions and optimize our marketing.</li>
            </ul>
            
            <h3 className="heading-4" style={{ marginTop: '20px', marginBottom: '12px', fontSize: '16px', fontWeight: '600' }}>Data Collected Through Tracking Technologies</h3>
            <p className="body-regular">These technologies may collect:</p>
            <ul className="legal-list">
              <li>IP address and approximate location</li>
              <li>Browser type and device information</li>
              <li>Pages visited and time spent on each page</li>
              <li>Referring website or source</li>
              <li>Actions taken on our site (e.g., form submissions, button clicks)</li>
            </ul>
            
            <h3 className="heading-4" style={{ marginTop: '20px', marginBottom: '12px', fontSize: '16px', fontWeight: '600' }}>How We Use This Data</h3>
            <ul className="legal-list">
              <li>To improve website performance and user experience</li>
              <li>To understand which content and services are most valuable to visitors</li>
              <li>To measure advertising effectiveness and optimize campaigns</li>
              <li>To deliver relevant advertisements on third-party platforms</li>
            </ul>
            
            <h3 className="heading-4" style={{ marginTop: '20px', marginBottom: '12px', fontSize: '16px', fontWeight: '600' }}>Managing Your Cookie Preferences</h3>
            <p className="body-regular">
              You can control and manage cookies through your browser settings. Most browsers 
              allow you to block or delete cookies, though this may affect your experience on 
              our website. Here's how to manage cookies in common browsers:
            </p>
            <ul className="legal-list">
              <li><strong>Chrome:</strong> Settings → Privacy and Security → Cookies and other site data</li>
              <li><strong>Safari:</strong> Preferences → Privacy → Manage Website Data</li>
              <li><strong>Firefox:</strong> Options → Privacy & Security → Cookies and Site Data</li>
              <li><strong>Edge:</strong> Settings → Cookies and site permissions</li>
            </ul>
            
            <h3 className="heading-4" style={{ marginTop: '20px', marginBottom: '12px', fontSize: '16px', fontWeight: '600' }}>Third-Party Tracking Services</h3>
            <p className="body-regular">
              We use the following third-party services that may set their own cookies:
            </p>
            <ul className="legal-list">
              <li><strong>Google Analytics:</strong> Web analytics service. Learn more and opt out at <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="legal-link">https://tools.google.com/dlpage/gaoptout</a></li>
              <li><strong>Meta Pixel (Facebook/Instagram):</strong> Advertising and conversion tracking. Manage your ad preferences at <a href="https://www.facebook.com/adpreferences" target="_blank" rel="noopener noreferrer" className="legal-link">https://www.facebook.com/adpreferences</a></li>
            </ul>
            <p className="body-regular">
              You can also opt out of interest-based advertising from many companies through the 
              Digital Advertising Alliance at <a href="https://optout.aboutads.info" target="_blank" rel="noopener noreferrer" className="legal-link">https://optout.aboutads.info</a>.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="heading-3">SMS Communications</h2>
            <p className="body-regular">
              Glow Health LLC may send SMS messages to individuals who have voluntarily provided 
              their phone number and explicitly opted in through our website forms.
            </p>
            <p className="body-regular">
              By providing your phone number and checking the SMS consent box, you agree to receive 
              text messages related to appointment scheduling, appointment reminders, account 
              notifications, and customer support.
            </p>
            <p className="body-regular">
              Message frequency varies. Message and data rates may apply.
            </p>
            <p className="body-regular">
              You may opt out at any time by replying STOP to any message. For assistance, reply 
              HELP or contact us at support@myglowhealth.co.
            </p>
            <p className="body-regular">
              Mobile information will not be shared with third parties for marketing purposes.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="heading-3">Privacy and Security Practices</h2>
            <p className="body-regular">
              Glow Health follows HIPAA-aligned privacy and security best practices to protect 
              your information. Our safeguards include:
            </p>
            <ul className="legal-list">
              <li>Encrypted data transmission and storage</li>
              <li>Secure access controls and authentication</li>
              <li>Regular security assessments</li>
              <li>Staff training on privacy and confidentiality</li>
              <li>Careful vetting of third-party service providers</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2 className="heading-3">Your Rights</h2>
            <p className="body-regular">You have the right to:</p>
            <ul className="legal-list">
              <li><strong>Access:</strong> Request a copy of the personal and health information we hold about you</li>
              <li><strong>Correction:</strong> Ask us to correct any inaccurate information</li>
              <li><strong>Deletion:</strong> Request deletion of your information, subject to legal and operational requirements</li>
              <li><strong>Questions:</strong> Contact us with any questions or concerns about your privacy</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2 className="heading-3">Updates to This Policy</h2>
            <p className="body-regular">
              We may update this Privacy Policy from time to time. We will notify you of any 
              significant changes by posting the updated policy on our website with a new 
              effective date.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="heading-3">Governing Law</h2>
            <p className="body-regular">
              This Privacy Policy is governed by the laws of the State of Utah.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="heading-3">Mobile Information Sharing</h2>
            <p className="body-regular">
              Glow Health LLC does not share, sell, rent, or trade mobile phone numbers or SMS 
              consent information with third parties for marketing or promotional purposes.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="heading-3">Contact Us</h2>
            <p className="body-regular">
              If you have questions about this Privacy Policy or how we handle your information, 
              please contact us at:
            </p>
            <p className="body-regular legal-contact">
              <strong>Email:</strong> support@myglowhealth.co
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
